// utils/encryption.js
import CryptoJS from 'crypto-js';

/**
 * Secure encryption utility for localStorage
 * Uses AES encryption with a session-based key
 */

// Generate or retrieve encryption key
const getEncryptionKey = () => {
  // Use a session-based key that's cleared on browser close
  let key = sessionStorage.getItem('_app_ek');
  
  if (!key) {
    // Generate a random 256-bit key
    key = CryptoJS.lib.WordArray.random(256/8).toString();
    sessionStorage.setItem('_app_ek', key);
  }
  
  return key;
};

/**
 * Encrypt data before storing in localStorage
 * @param {any} data - Data to encrypt (will be JSON stringified)
 * @returns {string} - Encrypted string
 */
export const encryptData = (data) => {
  try {
    const key = getEncryptionKey();
    const jsonString = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, key).toString();
    return encrypted;
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypt data from localStorage
 * @param {string} encryptedData - Encrypted string from localStorage
 * @returns {any} - Decrypted and parsed data
 */
export const decryptData = (encryptedData) => {
  try {
    if (!encryptedData) {
      return null;
    }
    
    const key = getEncryptionKey();
    const bytes = CryptoJS.AES.decrypt(encryptedData, key);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      console.warn('Decryption returned empty string');
      return null;
    }
    
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

/**
 * Securely store data in localStorage with encryption
 * @param {string} key - Storage key
 * @param {any} data - Data to store
 */
export const setSecureItem = (key, data) => {
  try {
    const encrypted = encryptData(data);
    localStorage.setItem(key, encrypted);
    return true;
  } catch (error) {
    console.error('Failed to store encrypted data:', error);
    return false;
  }
};

/**
 * Retrieve and decrypt data from localStorage
 * @param {string} key - Storage key
 * @returns {any} - Decrypted data or null
 */
export const getSecureItem = (key) => {
  try {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) {
      return null;
    }
    return decryptData(encrypted);
  } catch (error) {
    console.error('Failed to retrieve encrypted data:', error);
    return null;
  }
};

/**
 * Remove item from localStorage
 * @param {string} key - Storage key
 */
export const removeSecureItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Failed to remove item:', error);
    return false;
  }
};

/**
 * Clear encryption key (call on logout)
 */
export const clearEncryptionKey = () => {
  try {
    sessionStorage.removeItem('_app_ek');
    return true;
  } catch (error) {
    console.error('Failed to clear encryption key:', error);
    return false;
  }
};

/**
 * Clear all app data and encryption key
 */
export const clearAllSecureData = () => {
  try {
    // Clear specific app data
    localStorage.removeItem('p10-app-state');
    
    // Clear encryption key
    clearEncryptionKey();
    
    // Optionally clear all localStorage
    // localStorage.clear();
    
    return true;
  } catch (error) {
    console.error('Failed to clear secure data:', error);
    return false;
  }
};

/**
 * Hash sensitive data (one-way, for verification only)
 * @param {string} data - Data to hash
 * @returns {string} - SHA256 hash
 */
export const hashData = (data) => {
  return CryptoJS.SHA256(data).toString();
};

export default {
  encryptData,
  decryptData,
  setSecureItem,
  getSecureItem,
  removeSecureItem,
  clearEncryptionKey,
  clearAllSecureData,
  hashData
};